import * as R from 'ramda'
import moment from 'moment'
import { createSelector } from 'reselect'
import {
  DEPARTURE_CITIES_FILTERS_KEY,
  DEPARTURE_DATES_FILTERS_KEY,
  DURATIONS_FILTERS_KEY,
  TRANSPORTATION_FILTERS_KEY
} from 'constants/filters'
import { getFiltersQueryIntoValues } from 'domain/filters'
import { FilteredPackage } from 'types/typescript'
import { getQueryString } from './router'
import { getBeginDate, getQueryFiltersParams } from './sale'
import {
  DepartureDateOffset,
  DepartureDatesSettings,
  GetAreFiltersInitialized,
  GetDepartureCitiesFiltersFromQuery,
  GetDepartureCitiesFiltersFromReducer,
  GetDepartureDatesCardRanges,
  GetDepartureDatesMPRanges,
  GetDepartureDatesRanges,
  GetDepartureDatesSidebarRanges,
  GetDurationsFiltersFromQuery,
  GetFilters,
  GetFiltersFromQuery,
  GetFiltersProducts,
  GetHasDepartureDateFlexibilityBeenUsed,
  GetIsDepartureCitiesDisabled,
  GetIsFiltersRequestPending,
  GetSelectedCalendarDepartureDatesFlexibility,
  GetSelectedDepartureDateFromStoreKey,
  GetSelectedDepartureDateOffset,
  GetSelectedDepartureDateOffsetFromQuery,
  GetSelectedDepartureDateOffsetFromStoreKey,
  GetSelectedDepartureDatesFlexibilityFromQuery,
  GetSelectedDepartureDatesSettings,
  GetSelectedDepartureDatesSettingsFromQuery,
  GetSelectedDurationsFiltersFromReducer,
  GetSelectedFilters,
  GetSelectedFiltersNumber,
  GetSelectedFiltersNumberPerCategory,
  GetSelectedTransportation,
  GetSelectedtransportationFromQuery,
  QueryFiltersParams
} from './filters.types'
import { getIsHideReferencePriceFeatureEnabled } from './application'

export const getFilters: GetFilters = R.prop('filters')

export const getFiltersProducts: GetFiltersProducts = createSelector(
  getFilters,
  getIsHideReferencePriceFeatureEnabled,
  (filters, shouldHideReferencePrice) => {
    if (!shouldHideReferencePrice) return filters.products

    const result = {}
    for (const [key, value] of Object.entries(filters.products)) {
      const packages = (result[key]?.packages ?? [] as FilteredPackage[]).map(p => {
        return { ...p, msrp: null }
      })
      result[key] = { ...value, msrp: null, packages: packages }
    }
    return result
  }
)

export const getIsFiltersRequestPending: GetIsFiltersRequestPending = createSelector(
  getFilters,
  R.prop('isPending')
)

export const getAreFiltersInitialized: GetAreFiltersInitialized = createSelector(
  getFilters,
  R.prop('areFiltersInitialized')
)

export const getFiltersFromQuery: GetFiltersFromQuery = createSelector(
  getQueryString,
  getQueryFiltersParams,
  (queryString: string, queryFiltersParams: QueryFiltersParams) => (
    R.ifElse(
      () => R.isEmpty(queryFiltersParams),
      () => ({}),
      R.pipe(
        getFiltersQueryIntoValues,
        R.keys,
        R.reduce((acc, filterKey) => {
          if (queryFiltersParams[filterKey] !== undefined) {
            acc[filterKey] = (
              queryFiltersParams[filterKey]
                .split(',')
                .filter(R.compose(R.not, R.isEmpty))
            )
          }
          return acc
        }, {})
      )
    )(queryString)
  )
)

export const getDepartureCitiesFiltersFromQuery: GetDepartureCitiesFiltersFromQuery = createSelector(
  getFiltersFromQuery,
  R.pipe(
    R.prop(DEPARTURE_CITIES_FILTERS_KEY),
    R.defaultTo([])
  )
)

export const getDurationsFiltersFromQuery: GetDurationsFiltersFromQuery = createSelector(
  getFiltersFromQuery,
  R.pipe(
    R.prop(DURATIONS_FILTERS_KEY),
    R.defaultTo([]),
    R.map(offset => parseInt(offset, 10))
  )
)

export const getSelectedFilters: GetSelectedFilters = createSelector(
  getFilters,
  R.pipe(
    R.prop('selectedFilters'),
    R.defaultTo({})
  )
)

export const getSelectedTransportation: GetSelectedTransportation = createSelector(
  getFilters,
  R.pipe(
    R.prop('selectedTransportation'),
    R.defaultTo('all')
  )
)

export const getIsDepartureCitiesDisabled: GetIsDepartureCitiesDisabled = createSelector(
  getSelectedTransportation,
  (selectedTransportation) => selectedTransportation === 'none'
)

export const getDepartureCitiesFiltersFromReducer: GetDepartureCitiesFiltersFromReducer = createSelector(
  getSelectedFilters,
  R.pipe(
    R.prop(DEPARTURE_CITIES_FILTERS_KEY),
    R.defaultTo([])
  )
)

export const getSelectedDurationsFiltersFromReducer: GetSelectedDurationsFiltersFromReducer = createSelector(
  getSelectedFilters,
  R.pipe(
    R.prop(DURATIONS_FILTERS_KEY),
    R.defaultTo([])
  )
)

export const getSelectedDepartureDatesSettingsFromQuery: GetSelectedDepartureDatesSettingsFromQuery = createSelector(
  getFiltersFromQuery,
  R.pipe(
    R.prop(DEPARTURE_DATES_FILTERS_KEY),
    R.defaultTo([]),
    R.map(offset => parseInt(offset, 10))
  )
)

export const getSelectedDepartureDatesSettings: GetSelectedDepartureDatesSettings = createSelector(
  getSelectedFilters,
  R.pipe(
    R.prop(DEPARTURE_DATES_FILTERS_KEY),
    R.defaultTo([]),
    R.map(offset => parseInt(offset, 10))
  )
)

export const getHasDepartureDateFlexibilityBeenUsed: GetHasDepartureDateFlexibilityBeenUsed = createSelector(
  getFilters,
  R.prop('hasDepartureDateFlexibilityBeenUsed')
)

export const getSelectedDepartureDatesFlexibilityFromQuery: GetSelectedDepartureDatesFlexibilityFromQuery = createSelector(
  getSelectedDepartureDatesSettingsFromQuery,
  R.last // Should always be the second element and not the LAST one
)

export const getSelectedDepartureDateOffsetFromQuery: GetSelectedDepartureDateOffsetFromQuery = createSelector(
  getSelectedDepartureDatesSettingsFromQuery,
  R.head // Should always be the first element and not the HEADING one
)

export const getSelectedCalendarDepartureDatesFlexibility: GetSelectedCalendarDepartureDatesFlexibility = createSelector(
  getFilters,
  R.prop('departureDateFlexibility')
)

export const getSelectedDepartureDateOffsetFromStoreKey: GetSelectedDepartureDateOffsetFromStoreKey = createSelector(
  getFilters,
  R.prop('departureDateOffset')
)

export const getSelectedDepartureDateOffset: GetSelectedDepartureDateOffset = createSelector(
  getSelectedFilters,
  R.pipe(
    R.prop(DEPARTURE_DATES_FILTERS_KEY),
    (departureDateAndFlexibility: DepartureDatesSettings) => (
      (departureDateAndFlexibility?.length)
        ? [departureDateAndFlexibility[0]]
        : []
    )
  )
)

export const getSelectedDepartureDateFromStoreKey: GetSelectedDepartureDateFromStoreKey = createSelector(
  getSelectedDepartureDateOffsetFromStoreKey,
  getBeginDate,
  (dateOffset: DepartureDateOffset, dateOrigin: string) => (
    dateOffset
      ? moment(dateOrigin).add(parseInt(dateOffset as unknown as string, 10), 'days') /* Type cast to avoid any unwanted regression even if the type looks good */
      : null
  )
)

export const getSelectedFiltersNumber: GetSelectedFiltersNumber = createSelector(
  getSelectedFilters,
  filtersParams => Object
    .keys(filtersParams)
    .reduce((total, key) => {
      if (key !== DEPARTURE_DATES_FILTERS_KEY) {
        return (total + (filtersParams[key].length as number))
      }
      return (total + (filtersParams[key].length ? 1 : 0))
    }, 0)
)

export const getSelectedFiltersNumberPerCategory: GetSelectedFiltersNumberPerCategory = createSelector(
  getSelectedFilters,
  filtersParams => Object
    .keys(filtersParams)
    .reduce((acc, key) => {
      if (key !== DEPARTURE_DATES_FILTERS_KEY) {
        return ({ ...acc, [key]: filtersParams[key].length })
      }
      return ({ ...acc, [key]: (filtersParams[key].length ? 1 : 0) })
    }, {})
)

export const getDepartureDatesRanges: GetDepartureDatesRanges = createSelector(
  getBeginDate,
  (beginDate: string) => {
    const departureDateOption1 = moment().add(5, 'days')
    const departureDateOption2 = moment().add(14, 'days')
    const departureDateOption3 = moment().add(30, 'days')

    return ([{
      date: departureDateOption1,
      label: '',
      value: [departureDateOption1.diff(beginDate, 'days'), 3]
    }, {
      date: departureDateOption2,
      label: '',
      value: [departureDateOption2.diff(beginDate, 'days'), 3]
    }, {
      date: departureDateOption3,
      label: '',
      value: [departureDateOption3.diff(beginDate, 'days'), 3]
    }])
  }
)

export const getDepartureDatesCardRanges: GetDepartureDatesCardRanges = createSelector(
  getDepartureDatesRanges,
  (departureDatesRanges) => (
    departureDatesRanges.map((range, index) => ({
      ...range,
      label: `catalog.filters.card.departure_dates.pill${(index + 1).toString()}`
    }))
  )
)

export const getDepartureDatesSidebarRanges: GetDepartureDatesSidebarRanges = createSelector(
  getDepartureDatesRanges,
  (departureDatesRanges) => (
    departureDatesRanges.map((range, index) => ({
      ...range,
      label: `catalog.filters.sidebar.departure_dates.checkbox.option${(index + 1).toString()}`
    }))
  )
)

export const getDepartureDatesMPRanges: GetDepartureDatesMPRanges = createSelector(
  getDepartureDatesRanges,
  (departureDatesRanges) => {
    const labels = [
      'Imminent departure',
      'In 15 days',
      'In 1 month'
    ]

    if (departureDatesRanges.length <= labels.length) {
      return departureDatesRanges.map((range, index) => ({
        ...range,
        label: labels[index]
      }))
    }

    return departureDatesRanges
  }
)

export const getSelectedTransportationFromQuery: GetSelectedtransportationFromQuery = createSelector(
  getFiltersFromQuery,
  (filters) => filters[TRANSPORTATION_FILTERS_KEY]?.[0] ?? undefined
)
